import { z } from 'zod';
/**
 * Тип клиента — дискриминатор для четырёх категорий по ПП РФ № 1317 п.3 а).
 * Значения совпадают с enum `ClientType` в Prisma-схеме.
 */
export const clientTypeSchema = z.enum(['INDIVIDUAL', 'ENTREPRENEUR', 'LEGAL_RU', 'LEGAL_FOREIGN']);
const innRu = z
    .string()
    .regex(/^\d{10}$|^\d{12}$/, 'ИНН должен содержать 10 или 12 цифр');
const kppRu = z.string().regex(/^\d{9}$/, 'КПП должен содержать ровно 9 цифр');
const ogrnRu = z.string().regex(/^\d{13}$/, 'ОГРН должен содержать ровно 13 цифр');
const ogrnipRu = z.string().regex(/^\d{15}$/, 'ОГРНИП должен содержать ровно 15 цифр');
const innIp = z.string().regex(/^\d{12}$/, 'ИНН ИП должен содержать 12 цифр');
/**
 * Профиль ФЛ (резидент/нерезидент).
 *
 * @remarks Закрывает «физическое лицо» из ПП РФ № 1317 п.3 а).
 */
export const clientIndividualSchema = z.object({
    type: z.literal('INDIVIDUAL'),
    firstName: z.string().min(1).max(100),
    lastName: z.string().min(1).max(100),
    middleName: z.string().max(100).optional(),
    passportSerial: z.string().min(1).max(20),
    passportNumber: z.string().min(1).max(20),
    passportIssueDate: z.string().date(),
    passportIssuedBy: z.string().min(1).max(500),
    registrationAddress: z.string().min(1).max(500),
    actualAddress: z.string().max(500).optional(),
});
/**
 * Профиль ИП.
 *
 * @remarks Закрывает «индивидуального предпринимателя» из ПП РФ № 1317 п.3 а).
 */
export const clientEntrepreneurSchema = z.object({
    type: z.literal('ENTREPRENEUR'),
    firstName: z.string().min(1).max(100),
    lastName: z.string().min(1).max(100),
    middleName: z.string().max(100).optional(),
    inn: innIp,
    ogrnip: ogrnipRu,
    registrationAddress: z.string().min(1).max(500),
});
/**
 * Профиль ЮЛ РФ.
 *
 * @remarks Закрывает «юридическое лицо РФ» из ПП РФ № 1317 п.3 а).
 */
export const clientLegalRuSchema = z.object({
    type: z.literal('LEGAL_RU'),
    fullName: z.string().min(1).max(500),
    shortName: z.string().max(500).optional(),
    inn: innRu,
    kpp: kppRu,
    ogrn: ogrnRu,
    legalAddress: z.string().min(1).max(500),
    directorName: z.string().min(1).max(200),
    contactPersonName: z.string().max(200).optional(),
    contactPersonPhone: z.string().max(50).optional(),
    contactPersonEmail: z.string().email().optional(),
});
/**
 * Профиль иностранной организации.
 *
 * @remarks Закрывает «иностранную организацию» из ПП РФ № 1317 п.3 а).
 */
export const clientLegalForeignSchema = z.object({
    type: z.literal('LEGAL_FOREIGN'),
    fullName: z.string().min(1).max(500),
    shortName: z.string().max(500).optional(),
    country: z.string().min(1).max(100),
    taxId: z.string().min(1).max(50),
    registrationId: z.string().max(50).optional(),
    address: z.string().min(1).max(500),
    ruBranch: z.string().max(500).optional(),
    contactPersonName: z.string().max(200).optional(),
    contactPersonPhone: z.string().max(50).optional(),
    contactPersonEmail: z.string().email().optional(),
});
/**
 * Профиль клиента: discriminated union по полю `type`.
 *
 * Используется в:
 * - {@link registerClientSchema} как часть payload регистрации;
 * - PATCH /clients/me для редактирования профиля (все поля type-specific опциональны
 *   — см. {@link clientProfilePatchSchema}).
 */
export const clientProfileSchema = z.discriminatedUnion('type', [
    clientIndividualSchema,
    clientEntrepreneurSchema,
    clientLegalRuSchema,
    clientLegalForeignSchema,
]);
/**
 * Patch профиля — позволяет менять необязательные поля,
 * но НЕ тип клиента (смена типа = деактивация + новая регистрация).
 */
export const clientProfilePatchSchema = z.object({
    /** Отображаемое имя в UI (для всех типов). */
    displayName: z.string().min(1).max(200).optional(),
    phone: z.string().max(50).optional(),
    email: z.string().email().optional(),
    /** Фактический адрес местонахождения (общий для всех типов). */
    actualAddress: z.string().max(500).optional(),
    /** Код абонента ЭДО.МИГ24 — общий для всех типов. */
    edoSubscriberId: z.string().max(100).optional(),
    /**
     * Type-specific поля — можно патчить выборочно (зависит от type клиента).
     * Валидация конкретно по type — на сервере.
     */
    individual: clientIndividualSchema.omit({ type: true }).partial().optional(),
    entrepreneur: clientEntrepreneurSchema.omit({ type: true }).partial().optional(),
    legalRu: clientLegalRuSchema.omit({ type: true }).partial().optional(),
    legalForeign: clientLegalForeignSchema.omit({ type: true }).partial().optional(),
});
/** Запрос на прекращение регистрации (ПП РФ № 1317 п.3 д). */
export const deregisterRequestSchema = z.object({
    reason: z.string().max(2000).optional(),
});
//# sourceMappingURL=client-profile.js.map